import {
  RiDeleteBin6Line,
  RiDownload2Line,
  RiUpload2Line,
} from '@remixicon/react';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useIotsApi } from '../contexts/iotsApi';
import { useLanguage } from '../contexts/language';
import { AppFile } from '../types/core';
import { downloadFile } from '../utils/files';

interface FileUploaderProps {
  isError: boolean;
  files: AppFile[];
  onChange: (files: AppFile[]) => void;
  onDelete: (id: string) => void;
  disabled?: boolean;
}

const MAXIMUM_FILE_SIZE_MB = 20;

const FileUploader = ({
  isError,
  onChange,
  onDelete,
  files,
  disabled = false,
}: FileUploaderProps) => {
  const { t } = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const { getFile } = useIotsApi();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;

    const validFiles: AppFile[] = [];
    let hasInvalid = false;

    Array.from(selectedFiles).forEach((file, index) => {
      const isValidFormat = [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'image/jpg',
      ].includes(file.type);
      if (file.size <= MAXIMUM_FILE_SIZE_MB * 1024 * 1024 && isValidFormat) {
        validFiles.push({
          id: `${file.name}-${index}-${Date.now()}`,
          name: file.name,
          file,
        });
      } else {
        hasInvalid = true;
      }
    });

    if (hasInvalid) {
      enqueueSnackbar({
        variant: 'error',
        message: t('content.files-upload-error'),
      });
    }

    onChange(validFiles);
  };

  const handleDelete = (id: string): void => {
    onDelete(id);
  };

  const handleDownload = async (
    file: File | string,
    fileName: string,
  ): Promise<void> => {
    let fileToDownload: File;

    if (typeof file === 'string') {
      // Assume it's a direct download link from the backend, so we actually want the actual file object
      fileToDownload = await getFile(file);
    } else {
      fileToDownload = file;
    }

    downloadFile(fileToDownload, fileName);
  };

  return (
    <div>
      {!disabled && (
        <label
          className={`border ${isError ? 'border-red-500 text-red-500' : 'border-green-900 text-green-900'} font-medium rounded-lg px-4 py-2 mt-2 flex items-center space-x-2 hover:bg-green-50 transition cursor-pointer w-fit`}
        >
          <RiUpload2Line className="w-4 h-4" />
          <span>{t('content.upload')}</span>
          <input
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled}
          />
        </label>
      )}

      <div className="mt-4">
        {files.map(({ id, name, file }) => (
          <div key={id} className="flex items-center mb-2">
            <span className="mr-4">{name}</span>
            <button
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                void handleDownload(file, name);
              }}
              className="mr-2 text-gray-700 hover:text-black"
            >
              <RiDownload2Line className="w-5 h-5" />
            </button>
            {!disabled && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleDelete(id);
                }}
                className="text-gray-700 hover:text-red-600"
              >
                <RiDeleteBin6Line className="w-5 h-5" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FileUploader;
